/**
 * Shared reactive clock using requestAnimationFrame.
 *
 * Single rAF loop shared by all consumers (DuelCard list, DuelDetail countdown).
 * Only updates when the displayed second changes. Immediately syncs on tab
 * visibility change so countdowns never show stale values.
 */

let now = $state(Date.now());
let rafId: number | null = null;
let subscribers = 0;
let lastSecond = Math.floor(Date.now() / 1000);

function tick() {
  const current = Date.now();
  const currentSecond = Math.floor(current / 1000);
  if (currentSecond !== lastSecond) {
    lastSecond = currentSecond;
    now = current;
  }
  rafId = requestAnimationFrame(tick);
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    now = Date.now();
    lastSecond = Math.floor(now / 1000);
  }
}

function start() {
  if (rafId === null) {
    now = Date.now();
    lastSecond = Math.floor(now / 1000);
    rafId = requestAnimationFrame(tick);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
}

function stop() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
}

/** Subscribe to the shared clock. Call the returned function to unsubscribe. */
export function useClock(): { readonly now: number } {
  subscribers++;
  if (subscribers === 1) start();

  $effect(() => {
    return () => {
      subscribers--;
      if (subscribers <= 0) {
        subscribers = 0;
        stop();
      }
    };
  });

  return {
    get now() {
      return now;
    },
  };
}
